import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, ListGroup, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { tripService, articleService, UPLOADS_BASE_URL } from '../services/api';
import './SearchBar.css';

const SearchBar = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ trips: [], articles: [] });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce search
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults({ trips: [], articles: [] });
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(query);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    try {
      const [tripsRes, articlesRes] = await Promise.all([
        tripService.getAllTrips({ search: searchQuery, limit: 5 }),
        articleService.getArticles({ search: searchQuery, limit: 5 })
      ]);

      setResults({
        trips: tripsRes.data || [],
        articles: articlesRes.data.articles || []
      });
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setResults({ trips: [], articles: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setShowResults(false);
      setQuery('');
      if (onClose) onClose();
    }
  };

  const handleResultClick = (type, id, slug) => {
    const url = type === 'trip' ? `/trips/${id}` : `/blog/${slug}`;
    navigate(url);
    setShowResults(false);
    setQuery('');
    if (onClose) onClose();
  };

  const handleClear = () => {
    setQuery('');
    setResults({ trips: [], articles: [] });
    setShowResults(false);
    inputRef.current?.focus();
  };

  const totalResults = results.trips.length + results.articles.length;

  return (
    <div className="search-bar-container" ref={searchRef}>
      <Form onSubmit={handleSubmit}>
        <InputGroup className="search-input-group">
          <InputGroup.Text className="search-icon">
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            ref={inputRef}
            type="text"
            id="search-input"
            name="search"
            placeholder="Search trips, articles, destinations..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setShowResults(true)}
            className="search-input"
            autoComplete="off"
          />
          {query && (
            <InputGroup.Text 
              className="search-clear"
              onClick={handleClear}
              role="button"
            >
              <FaTimes />
            </InputGroup.Text>
          )}
          {loading && (
            <InputGroup.Text className="search-loading">
              <Spinner animation="border" size="sm" />
            </InputGroup.Text>
          )}
        </InputGroup>
      </Form>

      {showResults && query.length >= 2 && (
        <div className="search-results-dropdown">
          {totalResults === 0 && !loading ? (
            <div className="search-no-results">
              <p>No results found for "{query}"</p>
              <small>Try different keywords or browse all content</small>
            </div>
          ) : (
            <>
              {results.trips.length > 0 && (
                <div className="search-section">
                  <div className="search-section-header">Trips</div>
                  <ListGroup variant="flush">
                    {results.trips.map((trip) => (
                      <ListGroup.Item
                        key={trip._id}
                        action
                        onClick={() => handleResultClick('trip', trip._id)}
                        className="search-result-item"
                      >
                        <div className="d-flex align-items-center">
                          <div className="search-result-image me-3">
                            <img 
                              src={`${UPLOADS_BASE_URL}/uploads/${trip.image}`}
                              alt={trip.name}
                              onError={(e) => { e.target.src = '/images/default-trip.jpg'; }}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div className="search-result-title">{trip.name}</div>
                            <div className="search-result-meta">
                              📍 {trip.location} • ${trip.price}
                            </div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              {results.articles.length > 0 && (
                <div className="search-section">
                  <div className="search-section-header">Articles</div>
                  <ListGroup variant="flush">
                    {results.articles.map((article) => (
                      <ListGroup.Item
                        key={article._id}
                        action
                        onClick={() => handleResultClick('article', article._id, article.slug)}
                        className="search-result-item"
                      >
                        <div className="d-flex align-items-center">
                          <div className="search-result-image me-3">
                            <img 
                              src={`${UPLOADS_BASE_URL}/uploads/${article.featuredImage}`}
                              alt={article.title}
                              onError={(e) => { e.target.src = '/images/default-article.jpg'; }}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div className="search-result-title">{article.title}</div>
                            <div className="search-result-meta">
                              ⏱️ {article.readTime} min read
                            </div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              {totalResults > 0 && (
                <div className="search-view-all" onClick={handleSubmit}>
                  View all results for "{query}" →
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
